if (!customElements.get('cart-upsell-offers')) {
  customElements.define('cart-upsell-offers',
    (function() {
      return class CartUpsellOffers extends HTMLElement {
        constructor() {
          super();

          this.initializeElements();

          this.freeShippingThreshold = 5900; // $59.00 in cents

          this.boundCheckCartAndUpdateVisibility = this.checkCartAndUpdateVisibility.bind(this);

          this.addButtons.forEach(button => {
            button.addEventListener('click', this.onAddProductClick.bind(this));
          });

          this.initializeCartCheck();
          this.setupCartEventListeners();
        }

        // Handles initial cart check, polling for Shopify global variables if not yet available
        initializeCartCheck() {
          if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.routes !== 'undefined' && typeof window.fetchConfig !== 'undefined') {
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
                setTimeout(this.boundCheckCartAndUpdateVisibility, 500);
            } else {
                document.addEventListener('DOMContentLoaded', () => {
                    setTimeout(this.boundCheckCartAndUpdateVisibility, 500);
                }, { once: true });
            }
          } else {
            setTimeout(() => this.initializeCartCheck(), 100); // Poll every 100ms
          }
        }

        initializeElements() {
          this.thresholdSelector = this.querySelector('.threshold-selector');
          this.addButtons = this.querySelectorAll('.add-button[data-variant]');
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          if (!this.thresholdSelector) {
            console.warn('[CartUpsellOffers] Threshold selector not found.');
          }
          if (this.addButtons.length === 0) {
            console.warn('[CartUpsellOffers] No add buttons found with data-variant attribute.');
          }
          if (!this.cart) {
            console.warn('[CartUpsellOffers] Cart element (cart-notification or cart-drawer) not found.');
          }
        }

        setupCartEventListeners() {
          // Listen for various Shopify cart update events
          document.addEventListener('cart:update', this.boundCheckCartAndUpdateVisibility);
          document.addEventListener('cart:refresh', this.boundCheckCartAndUpdateVisibility);
          document.addEventListener('cart:change', this.boundCheckCartAndUpdateVisibility);
          document.addEventListener('drawer:open', this.boundCheckCartAndUpdateVisibility);

          // Listen for Ajax requests completing, which may indicate cart updates
          document.addEventListener('ajaxComplete', event => {
            var xhrUrl = event.detail ? event.detail[0].responseURL : '';
            if (xhrUrl && (xhrUrl.includes('/cart/add') || xhrUrl.includes('/cart/update') || xhrUrl.includes('/cart/change'))) {
              setTimeout(this.boundCheckCartAndUpdateVisibility, 300);
            }
          });

          if (window.PUB_SUB_EVENTS) {
            document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, this.boundCheckCartAndUpdateVisibility);
          }

          window.addEventListener('load', () => {
            setTimeout(this.boundCheckCartAndUpdateVisibility, 500);
          }, { once: true });
        }

        connectedCallback() {
          this.initializeElements();
        }

        disconnectedCallback() {
          // Clean up event listeners when element is removed from DOM
          document.removeEventListener('cart:update', this.boundCheckCartAndUpdateVisibility);
          document.removeEventListener('cart:refresh', this.boundCheckCartAndUpdateVisibility);
          document.removeEventListener('cart:change', this.boundCheckCartAndUpdateVisibility);
          document.removeEventListener('drawer:open', this.boundCheckCartAndUpdateVisibility);

          if (window.PUB_SUB_EVENTS) {
            document.removeEventListener(window.PUB_SUB_EVENTS.cartUpdate, this.boundCheckCartAndUpdateVisibility);
          }
        }

        checkCartAndUpdateVisibility() {
          // Guard to ensure Shopify globals are defined before proceeding
          if (typeof window.Shopify === 'undefined' || typeof window.Shopify.routes === 'undefined' || typeof window.fetchConfig === 'undefined') {
              setTimeout(this.boundCheckCartAndUpdateVisibility, 200);
              return;
          }

          if (!this.thresholdSelector || this.addButtons.length === 0) {
            this.initializeElements();
            if (!this.thresholdSelector || this.addButtons.length === 0) {
              console.warn('[CartUpsellOffers] Missing critical elements after re-initialization. Cannot proceed with cart check.');
              return;
            }
          }

          Promise.all([
            this.getCurrentCartSubtotal(),
            this.getCartContentsForFiltering()
          ])
          .then(([cartSubtotal, cartContents]) => {
              // Show upsell offers when threshold IS met (opposite of free-shipping-offers)
              if (cartSubtotal >= this.freeShippingThreshold) {
                const productsShown = this.updateProductVisibility(cartContents);
                // Hide if all offers are already in cart
                if (productsShown > 0) {
                  this.showUpsellSelector();
                } else {
                  this.hideUpsellSelector();
                }
              } else {
                this.hideUpsellSelector();
              }
            })
            .catch(error => {
              console.error('[CartUpsellOffers] Error getting cart subtotal or contents:', error);
            });
        }

        getCurrentCartSubtotal() {
          const config = window.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          return fetch(window.Shopify.routes.root + 'cart.js', config)
            .then(response => response.json())
            .then(cart => {
              return cart.total_price;
            });
        }

        getCartContentsForFiltering() {
          const config = window.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          return fetch(window.Shopify.routes.root + 'cart.js', config)
            .then(response => response.json())
            .then(cart => {
              const presentVariantIds = new Set();
              if (cart && cart.items) {
                cart.items.forEach(item => {
                  presentVariantIds.add(item.variant_id);
                });
              }
              return presentVariantIds;
            });
        }

        updateProductVisibility(cartContents) {
          const productGrid = this.thresholdSelector.querySelector('.product-grid');
          if (!productGrid) {
              console.warn('[CartUpsellOffers] Product grid container not found. Cannot reorder products.');
              return 0;
          }
          const productBlocks = Array.from(productGrid.querySelectorAll('.product-block'));

          if (productBlocks.length === 0) {
            return 0;
          }

          // Filter out products already in cart
          let candidateProducts = productBlocks.map(block => {
            const button = block.querySelector('.add-button[data-price][data-variant]');
            if (!button) {
              console.warn('[CartUpsellOffers] Product block found without a .add-button with data-price or data-variant.');
              return null;
            }

            const variantId = parseInt(button.dataset.variant, 10);

            // Skip if the product variant is already in the cart
            if (cartContents.has(variantId)) {
                return null;
            }

            return {
              block: block,
              variantId: variantId
            };
          }).filter(item => item !== null);

          const maxProductsToShow = 4;
          let productsToShow = candidateProducts.slice(0, maxProductsToShow);

          // DOM Reordering and Display
          productBlocks.forEach(block => {
            block.style.display = 'none';
            block.classList.add('hidden');
            block.setAttribute('aria-hidden', 'true');
          });

          // Clear the current product grid to append in the new order
          while (productGrid.firstChild) {
              productGrid.removeChild(productGrid.firstChild);
          }

          // Append the products to show
          productsToShow.forEach(item => {
            productGrid.appendChild(item.block);
            item.block.style.display = 'flex';
            item.block.classList.remove('hidden');
            item.block.removeAttribute('aria-hidden');
          });

          return productsToShow.length;
        }

        showUpsellSelector() {
          if (this.thresholdSelector) {
            this.thresholdSelector.style.display = 'block';
            this.thresholdSelector.classList.remove('hidden');
            this.thresholdSelector.removeAttribute('aria-hidden');
          } else {
            console.warn('[CartUpsellOffers] Attempted to show upsell selector, but reference is lost or element not found.');
          }
        }

        hideUpsellSelector() {
          if (this.thresholdSelector) {
            this.thresholdSelector.classList.add('hidden');
            this.thresholdSelector.setAttribute('aria-hidden', 'true');

            // Use a timeout to wait for transition to complete
            setTimeout(() => {
              if (this.thresholdSelector) {
                this.thresholdSelector.style.display = 'none';
              }
            }, 300); // Match this to your CSS transition duration
          } else {
            console.warn('[CartUpsellOffers] Attempted to hide upsell selector, but reference is lost or element not found.');
          }
        }

        onAddProductClick(evt) {
          const button = evt.currentTarget;
          const variantId = button.dataset.variant;

          if (!variantId) {
            console.error('[CartUpsellOffers] No variant ID found on the clicked button.');
            return;
          }

          this.initializeElements();

          this.setButtonsLoading(true);

          this.addProductToCart(variantId)
            .then(response => {
              if (response.status && response.status !== 200) {
                console.error('[CartUpsellOffers] Failed to add product:', response.description || response);
                return;
              }

              if (this.cart) {
                this.cart.renderContents(response);
                this.verifyProductInCart(variantId);
              }
            })
            .catch(error => {
              console.error('[CartUpsellOffers] Error adding product to cart:', error);
            })
            .finally(() => {
              this.setButtonsLoading(false);
              setTimeout(this.boundCheckCartAndUpdateVisibility, 1000);
            });
        }

        setButtonsLoading(isLoading) {
          this.addButtons.forEach(button => {
            if (isLoading) {
              button.setAttribute('aria-disabled', 'true');
              button.classList.add('loading');

              let loadingSpinner = button.querySelector('.loading__spinner');
              if (!loadingSpinner) {
                loadingSpinner = document.createElement('div');
                loadingSpinner.classList.add('loading__spinner');
                button.appendChild(loadingSpinner);
              }
              loadingSpinner.classList.remove('hidden');
            } else {
              button.removeAttribute('aria-disabled');
              button.classList.remove('loading');
              const loadingSpinner = button.querySelector('.loading__spinner');
              if (loadingSpinner) loadingSpinner.classList.add('hidden');
            }
          });
        }

        addProductToCart(variantId) {
          const config = window.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);

          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map(section => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          } else {
             console.warn('[CartUpsellOffers] Cart element or its getSectionsToRender method not available. Sections not included in add to cart request.');
          }

          config.body = formData;

          return fetch(window.Shopify.routes.root + 'cart/add.js', config)
            .then(response => response.json());
        }

        verifyProductInCart(variantId) {
          setTimeout(() => {
            const cartItems = document.querySelectorAll('.cart-item[data-rev-variant]');
            const variantIds = Array.from(cartItems).map(item => item.dataset.revVariant);

            const productFound = variantIds.includes(variantId.toString());

            if (!productFound) {
              console.error('[CartUpsellOffers] Product verification failed: Added variant ID not found in cart DOM.', {
                addedVariantId: variantId,
                currentCartVariants: variantIds,
                cartItemCount: cartItems.length
              });
            } else {
              if (window.PUB_SUB_EVENTS && window.publish) {
                window.publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'cart-upsell-offers',
                  productVariantId: variantId
                });
              }
            }
          }, 500);
        }
      };
    })()
  );
}
